import React, { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { siteName } from '../utils/adminSite';
import { bookingApi } from '../utils/bookingApi';
import { shopApi } from '../utils/shopApi';
import useHasMyGymnasts from './useHasMyGymnasts';
import useHasDiary from './diary/useHasDiary';
import { useFeatures } from '../hooks/useFeature';
import ConnectionIndicator from './ConnectionIndicator';
import PullToRefresh from './PullToRefresh';
import PreviewBanner from './PreviewBanner';
import OpenClubBanner from './OpenClubBanner';
import BillingBanner from './BillingBanner';
import TermsPrompt from './TermsPrompt';
import GetStarted from './getStarted/GetStarted';
import { GetStartedProvider } from './getStarted/GetStartedContext';
import { isPreviewing, exitPreview } from '../utils/preview';
import { pendingCount } from '../utils/offline';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import './AppLayout.css';
import { ErrorBoundary } from './ui';

function getTotalCartCount() {
  let count = 0;
  try {
    const saved = sessionStorage.getItem('booking-cart');
    if (saved) count += JSON.parse(saved).reduce((s, [, g]) => s + g.length, 0);
  } catch {}
  try {
    const shopItems = JSON.parse(localStorage.getItem('shopCart')) || [];
    count += shopItems.reduce((s, item) => s + item.quantity, 0);
  } catch {}
  return count;
}

export default function AppLayout() {
  const {
    user, logout, loading,
    isAdult, isStaff, can,
  } = useAuth();
  const { branding, fetchBranding } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = isStaff;
  const has = useFeatures();
  const hasDiary = useHasDiary() && has('diary');
  const membershipsOn = has('memberships');
  const shopOn = has('shop');
  const hasMyGymnasts = useHasMyGymnasts();

  const navRef = useRef(null);

  // Branding is first fetched before anyone signs in, so fetch it again for
  // whoever has just signed in (their club's name and colours)
  const userId = user?.id;
  useEffect(() => {
    if (userId) fetchBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Keep --nav-height in sync with the nav bar's own height (banners and the
  // club's name loading change it without a window resize). It leaves out the
  // iPhone status bar space, which CSS adds with env(safe-area-inset-top):
  // iOS can report that late, and a padding change fires no ResizeObserver.
  useEffect(() => {
    const update = () => {
      const nav = navRef.current;
      if (nav) {
        const statusBar = parseFloat(window.getComputedStyle(nav).paddingTop) || 0;
        document.documentElement.style.setProperty('--nav-height', `${nav.offsetHeight - statusBar}px`);
      }
    };
    update();
    window.addEventListener('resize', update);
    const observer = typeof ResizeObserver !== 'undefined' && navRef.current ? new ResizeObserver(update) : null;
    if (observer) observer.observe(navRef.current);
    return () => {
      window.removeEventListener('resize', update);
      if (observer) observer.disconnect();
    };
    // The bar only appears once they're signed in, so measure again then
  }, [Boolean(user)]); // eslint-disable-line react-hooks/exhaustive-deps

  const [openDropdown, setOpenDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [noticeBanner, setNoticeBanner] = useState(false);
  const [paymentBanner, setPaymentBanner] = useState(null); // null | 'pending' | 'needs_method'
  const [hasOverdueCharge, setHasOverdueCharge] = useState(false);
  const [hasAnyCharge, setHasAnyCharge] = useState(false);
  const [cartCount, setCartCount] = useState(getTotalCartCount);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);

  const dropdownRef = useRef(null);

  // ── Close dropdown on outside click ────────────────────────────────────────
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Close menus on navigation ───────────────────────────────────────────────
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // ── Cart count ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => setCartCount(getTotalCartCount());
    window.addEventListener('booking-cart-update', handler);
    window.addEventListener('shop-cart-update', handler);
    return () => {
      window.removeEventListener('booking-cart-update', handler);
      window.removeEventListener('shop-cart-update', handler);
    };
  }, []);

  // ── Noticeboard unread count ────────────────────────────────────────────────
  const refreshUnreadCount = useCallback(() => {
    bookingApi.getNoticeboard()
      .then(r => {
        const unread = r.data.filter(p => !p.isRead).length;
        setUnreadCount(unread);
        setNoticeBanner(unread > 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (user) refreshUnreadCount();
  }, [user, refreshUnreadCount]);

  // ── Messages unread count (every 30s and when the app comes back) ──────────
  const [chatUnread, setChatUnread] = useState(0);
  const messaging = has('messaging');
  useEffect(() => {
    if (!user || !messaging) return undefined;
    const refresh = () => bookingApi.chatUnreadCount().then(r => setChatUnread(r.data.count || 0)).catch(() => {});
    refresh();
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [user, messaging]);
  const messagesLabel = `Messages${chatUnread > 0 ? ` (${chatUnread})` : ''}`;
  // Members' own kit to sell or give away; buyer and seller talk in Messages
  const communityOn = messaging && has('communityShop');
  // Staff who look after messages land on the staff view (families and groups)
  const messagesPath = isStaff && (can?.('communications', 'VIEW') || can?.('welfare', 'MANAGE'))
    ? '/booking/admin/chat' : '/booking/messages';

  // ── Payment / overdue banners (non-admin only) ──────────────────────────────
  useEffect(() => {
    if (!user || isAdmin || !membershipsOn) return;
    bookingApi.getMyMemberships()
      .then(r => {
        if (r.data.some(m => m.status === 'PENDING_PAYMENT')) setPaymentBanner('pending');
        else if (r.data.some(m => m.needsPaymentMethod)) setPaymentBanner('needs_method');
      })
      .catch(() => {});
  }, [user, isAdmin, membershipsOn]);

  useEffect(() => {
    if (!user || isAdmin) return;
    bookingApi.getMyCharges()
      .then(r => {
        const now = new Date();
        setHasOverdueCharge(r.data.some(c => new Date(c.dueDate) < now));
        setHasAnyCharge(r.data.length > 0);
      })
      .catch(() => {});
  }, [user, isAdmin]);

  // ── Pending shop orders count (staff who can see shop orders) ──────────────
  const seesShopOrders = isStaff && can('shop');
  useEffect(() => {
    if (!seesShopOrders || !shopOn) return;
    shopApi.getPendingOrderCount()
      .then(res => setPendingOrderCount(res.data.count))
      .catch(() => {});
  }, [seesShopOrders, shopOn]);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;

  const toggleDropdown = (name) => setOpenDropdown(o => o === name ? null : name);
  const closeMobile = () => setIsMobileMenuOpen(false);
  const handleLogout = () => {
    const waiting = pendingCount();
    if (waiting && !window.confirm(`${waiting} change${waiting === 1 ? " hasn't" : "s haven't"} been sent yet (no signal). They'll be sent next time you sign in on this phone. Log out anyway?`)) return;
    if (isPreviewing() && exitPreview('/dashboard')) return;
    logout();
    navigate('/');
  };

  const helpPath = isAdmin ? '/booking/admin/help' : '/booking/help';

  // Gymnast pages are grouped under one heading: a parent's gymnasts, or a
  // gymnast's own training when they sign in themselves
  const gymnastsLabel = isAdult || isStaff ? 'My gymnasts' : 'My training';
  const showProgress = !isStaff || hasMyGymnasts;
  // Requirements are linked from Competitions, so they only need their own
  // link when a club has competitions off
  const competitionsLink = has('competitions')
    ? { to: '/booking/competitions', label: 'Competitions' }
    : has('requirements') ? { to: '/booking/requirements', label: 'Requirements' } : null;

  // Club admins (or a super admin acting in a club) get a setup checklist.
  // Fails closed: a missing isHouseClub counts as the house club.
  const showGetStarted = user?.role === 'CLUB_ADMIN' && user?.club?.isHouseClub === false && has('getStarted');

  const tree = (
    <div className="app-layout">
      <nav className="app-layout__nav" ref={navRef}>

        {/* Top bar: brand + admin link + menu */}
        <div className="app-layout__topbar">
          <NavLink to="/dashboard" className="app-layout__brand">
            {/* The club's name stays beside its logo */}
            {branding?.logoUrl && <img src={branding.logoUrl} alt="" className="app-layout__brand-logo" />}
            <span className="app-layout__brand-name">{branding?.clubName || siteName()}</span>
          </NavLink>
          <div className="app-layout__user">
            {messaging && (
              <NavLink
                to={messagesPath}
                className={`app-layout__messages${chatUnread > 0 ? ' app-layout__messages--unread' : ''}`}
                aria-label={chatUnread > 0 ? `Messages, ${chatUnread} unread` : 'Messages'}
                onClick={() => setOpenDropdown(null)}
              >
                <ChatBubbleLeftRightIcon className="app-layout__messages-icon" aria-hidden="true" />
                {chatUnread > 0 && <span className="app-layout__badge">{chatUnread > 99 ? '99+' : chatUnread}</span>}
              </NavLink>
            )}
            {isStaff && (
              <NavLink
                to="/admin-hub"
                className="app-layout__link--admin"
                style={{ position: 'relative' }}
              >
                Admin
                {pendingOrderCount > 0 && <span className="app-layout__badge">{pendingOrderCount}</span>}
              </NavLink>
            )}
            <button
              className="app-layout__hamburger"
              onClick={() => setIsMobileMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          </div>
        </div>

        {/* Desktop nav links */}
        <div className="app-layout__links" ref={dropdownRef}>

          {/* Left group */}
          <div className="app-layout__links-left">
            <NavLink to="/dashboard" className="app-layout__link" end>Home</NavLink>

            {/* Staff keep a short bar on desktop: everything else is in Account,
                and their favourite tiles on the home page */}
            {!isStaff && (<>
            <NavLink to="/booking" end className="app-layout__link" onClick={() => setOpenDropdown(null)} state={{ skipAdminRedirect: true }}>Book a session</NavLink>
            <NavLink to="/booking/my-bookings" className="app-layout__link" onClick={() => setOpenDropdown(null)}>My Bookings</NavLink>

            {/* The gymnasts on this account */}
            <div className="app-layout__dropdown">
              <button
                className={`app-layout__dropdown-btn${openDropdown === 'gymnasts' ? ' active' : ''}`}
                onClick={() => toggleDropdown('gymnasts')}
              >
                {gymnastsLabel} ▾
              </button>
              {openDropdown === 'gymnasts' && (
                <div className="app-layout__dropdown-menu">
                  <NavLink to="/my-progress" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Progress</NavLink>
                  {hasDiary && <NavLink to="/my-diary" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Diary</NavLink>}
                  {competitionsLink && <NavLink to={competitionsLink.to} className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>{competitionsLink.label}</NavLink>}
                  <NavLink to="/booking/incidents" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>First Aid Reports</NavLink>
                </div>
              )}
            </div>

            {has('shop') && <NavLink to="/booking/shop" className="app-layout__link" onClick={() => setOpenDropdown(null)}>Shop</NavLink>}
            {communityOn && <NavLink to="/booking/community" className="app-layout__link" onClick={() => setOpenDropdown(null)}>Community shop</NavLink>}

            {/* Noticeboard */}
            <NavLink
              to="/booking/noticeboard"
              className="app-layout__link"
              style={{ position: 'relative' }}
              onClick={() => setNoticeBanner(false)}
            >
              Noticeboard
              {unreadCount > 0 && <span className="app-layout__badge">{unreadCount}</span>}
            </NavLink>
            {messaging && (
              <NavLink to="/booking/messages" className="app-layout__link" style={{ position: 'relative' }}>
                Messages
                {chatUnread > 0 && <span className="app-layout__badge">{chatUnread}</span>}
              </NavLink>
            )}
            </>)}

          </div>

          {/* Right group */}
          <div className="app-layout__links-right">
            {/* Cart */}
            {(cartCount > 0 || hasAnyCharge) && (
              <NavLink to="/booking/cart" className="app-layout__cart-link" onClick={() => setOpenDropdown(null)}>
                Cart{cartCount > 0 ? ` (${cartCount})` : ''}
              </NavLink>
            )}

            {/* Account */}
            <div className="app-layout__dropdown">
              <button
                className={`app-layout__dropdown-btn${openDropdown === 'account' ? ' active' : ''}`}
                onClick={() => toggleDropdown('account')}
              >
                Account ▾
                {isStaff && unreadCount + chatUnread > 0 && <span className="app-layout__badge">{unreadCount + chatUnread}</span>}
              </button>
              {openDropdown === 'account' && (
                <div className="app-layout__dropdown-menu app-layout__dropdown-menu--right">
                  {/* Staff get what any parent in the club sees; their staff pages are Admin tiles */}
                  {isStaff && <>
                    <NavLink to="/booking" end className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)} state={{ skipAdminRedirect: true }}>Book a session</NavLink>
                    <NavLink to="/booking/my-bookings" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>My Bookings</NavLink>
                    <NavLink to="/booking/noticeboard" className="app-layout__dropdown-item" onClick={() => { setOpenDropdown(null); setNoticeBanner(false); }}>
                      Noticeboard{unreadCount > 0 ? ` (${unreadCount})` : ''}
                    </NavLink>
                    {messaging && <NavLink to="/booking/messages" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>{messagesLabel}</NavLink>}
                    {showProgress && <NavLink to="/my-progress" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Progress</NavLink>}
                    {hasDiary && <NavLink to="/my-diary" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Diary</NavLink>}
                    {competitionsLink && <NavLink to={competitionsLink.to} className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>{competitionsLink.label}</NavLink>}
                    <NavLink to="/booking/incidents" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>First Aid Reports</NavLink>
                    {has('shop') && <NavLink to="/booking/shop" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Shop</NavLink>}
                    {communityOn && <NavLink to="/booking/community" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Community shop</NavLink>}
                    <div className="app-layout__dropdown-divider" />
                  </>}
                  <NavLink to="/booking/my-account" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>My Account</NavLink>
                  {has('notifications') && <NavLink to="/booking/notifications" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Notifications</NavLink>}
                  <NavLink to={helpPath} className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Help</NavLink>
                  {user?.isDeveloper && <NavLink to="/preview-as" className="app-layout__dropdown-item" onClick={() => setOpenDropdown(null)}>Preview as…</NavLink>}
                  <div className="app-layout__dropdown-divider" />
                  <button type="button" className="app-layout__dropdown-item app-layout__dropdown-logout" onClick={() => { setOpenDropdown(null); handleLogout(); }}>Log out</button>
                </div>
              )}
            </div>

          </div>

        </div>
      </nav>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div className="app-layout__mobile-overlay" onClick={closeMobile} />
      )}

      {/* Mobile slide-in menu */}
      <div className={`app-layout__mobile-menu${isMobileMenuOpen ? ' open' : ''}`}>
        <div className="app-layout__mobile-header">
          <span className="app-layout__mobile-title">Menu</span>
          <button className="app-layout__mobile-close" onClick={closeMobile} aria-label="Close menu">×</button>
        </div>
        <div className="app-layout__mobile-links">

          <NavLink to="/dashboard" className="app-layout__mobile-link" onClick={closeMobile} end>Home</NavLink>
          <NavLink to="/booking" end className="app-layout__mobile-link" onClick={closeMobile} state={{ skipAdminRedirect: true }}>Book a session</NavLink>
          <NavLink to="/booking/my-bookings" className="app-layout__mobile-link" onClick={closeMobile}>My Bookings</NavLink>
          <NavLink
            to="/booking/noticeboard"
            className="app-layout__mobile-link"
            onClick={() => { closeMobile(); setNoticeBanner(false); }}
          >
            Noticeboard{unreadCount > 0 ? ` (${unreadCount})` : ''}
          </NavLink>
          {messaging && <NavLink to="/booking/messages" className="app-layout__mobile-link" onClick={closeMobile}>{messagesLabel}</NavLink>}

          <div className="app-layout__mobile-section-label">{gymnastsLabel}</div>
          {showProgress && <NavLink to="/my-progress" className="app-layout__mobile-link" onClick={closeMobile}>Progress</NavLink>}
          {hasDiary && <NavLink to="/my-diary" className="app-layout__mobile-link" onClick={closeMobile}>Diary</NavLink>}
          {competitionsLink && <NavLink to={competitionsLink.to} className="app-layout__mobile-link" onClick={closeMobile}>{competitionsLink.label}</NavLink>}
          <NavLink to="/booking/incidents" className="app-layout__mobile-link" onClick={closeMobile}>First Aid Reports</NavLink>

          <div className="app-layout__mobile-divider" />

          {has('shop') && <NavLink to="/booking/shop" className="app-layout__mobile-link" onClick={closeMobile}>Shop</NavLink>}
          {communityOn && <NavLink to="/booking/community" className="app-layout__mobile-link" onClick={closeMobile}>Community shop</NavLink>}
          <NavLink to="/booking/my-account" className="app-layout__mobile-link" onClick={closeMobile}>My Account</NavLink>
          {has('notifications') && <NavLink to="/booking/notifications" className="app-layout__mobile-link" onClick={closeMobile}>Notifications</NavLink>}
          <NavLink to={helpPath} className="app-layout__mobile-link" onClick={closeMobile}>Help</NavLink>
          {user?.isDeveloper && <NavLink to="/preview-as" className="app-layout__mobile-link" onClick={closeMobile}>Preview as…</NavLink>}

        </div>
        <div className="app-layout__mobile-footer">
          <button className="app-layout__mobile-link app-layout__mobile-logout" onClick={handleLogout}>Log out</button>
        </div>
      </div>

      <div className="app-layout__content">
        <ConnectionIndicator />
        <PullToRefresh />
        <PreviewBanner />
        <OpenClubBanner />
        <BillingBanner />

        {/* Banners */}
        {noticeBanner && (
          <Link to="/booking/noticeboard" className="app-layout__notice-banner" onClick={() => setNoticeBanner(false)}>
            <span>📌 {unreadCount} new notice{unreadCount !== 1 ? 's' : ''} on the noticeboard</span>
            <span className="app-layout__banner-cta">View</span>
          </Link>
        )}

        {paymentBanner && (
          <Link to="/booking/my-account" className="app-layout__payment-banner">
            {paymentBanner === 'pending' ? (
              <><span>⚠ Membership payment required — your membership is not yet active.</span><span className="app-layout__banner-cta">Set up payment</span></>
            ) : (
              <><span>⚠ Payment method required — your membership won't renew without a card on file.</span><span className="app-layout__banner-cta">Add now</span></>
            )}
          </Link>
        )}

        {hasOverdueCharge && (
          <Link to="/booking/cart" className="app-layout__payment-banner">
            <span>⚠ You have an overdue charge — pay it to make new bookings.</span>
            <span className="app-layout__banner-cta">Pay now</span>
          </Link>
        )}

        <main className="app-layout__main">
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<div className="loading"><div className="spinner" /></div>}>
              <Outlet context={{ refreshUnreadCount }} />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      {showGetStarted && <GetStarted />}
      <TermsPrompt />
    </div>
  );
  return showGetStarted ? <GetStartedProvider>{tree}</GetStartedProvider> : tree;
}
